import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { PageBox } from "@/components/UI/page";
import { ActionHandler } from "@/components/UI/tools";
import { EmptyRow, Row, Section } from "@/app/workspace/settings/ui";
import { Add_new, Remove } from "@/components/UI/svgs";
import { Request } from "@/components/CRUD";
import { appState, UserPreference } from "@/app/appState";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { SpeakerProfileRecorder } from "@/components/SpeakerProfileRecorder";
import { appendProfile } from "@/utils/speakerProfileSerializer";
import { SkillsSection } from "./skillsSection";
import {
    createUserPreference,
    updateUserPreference,
    deleteUserPreference,
    CreateUserPreferenceData,
    UpdateUserPreferenceData,
    DeleteUserPreferenceData,
} from "@/api";

type CalendarSyncStatus = {
    google_connected: boolean;
    calendar_scope_granted: boolean;
    sync_enabled: boolean;
    synced_count: number;
    client_count: number;
    login_provider?: string | null;
};

const PreferenceEditor = observer(({ preference, onClose, onSaved }: {
    preference: UserPreference | null; // null = create new
    onClose: () => void;
    onSaved: () => void;
}) => {
    const [content, setContent] = useState(preference?.content ?? "");
    const [loading, setLoading] = useState(false);

    const create = Request<CreateUserPreferenceData, any>(
        createUserPreference,
        () => ({ body: { content } }),
        setLoading,
        () => { onSaved(); onClose(); },
        (error) => {
            appState.setModal({
                title: appState.t("common.error"),
                body: error.detail?.toString() ?? appState.t("common.somethingWentWrong"),
            });
        },
        () => content.trim().length > 0,
    );

    const update = Request<UpdateUserPreferenceData, any>(
        updateUserPreference,
        () => ({ body: { id: preference?.id ?? 0, content } }),
        setLoading,
        () => { onSaved(); onClose(); },
        (error) => {
            appState.setModal({
                title: appState.t("common.error"),
                body: error.detail?.toString() ?? appState.t("common.somethingWentWrong"),
            });
        },
        () => content.trim().length > 0,
    );

    const save = () => preference ? update(undefined as any) : create(undefined as any);

    return (
        <div className="flex flex-col gap-2 min-w-[300px]">
            <textarea
                className="textarea min-h-[120px]"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={appState.t("profile.enterPreference")}
                autoFocus
            />
            <div className="flex flex-row justify-end gap-1">
                <button
                    className="btn btn-ghost btn-sm"
                    onClick={onClose}
                    disabled={loading}
                >
                    {appState.t("common.cancel")}
                </button>
                <button
                    className="btn btn-soft btn-sm"
                    onClick={() => save()}
                    disabled={loading || content.trim().length === 0}
                >
                    {loading ? appState.t("common.saving") : preference ? appState.t("common.update") : appState.t("common.create")}
                </button>
            </div>
        </div>
    );
});

export const ProfilePage = observer(() => {
    const [loading, setLoading] = useState(false);
    const [calendarLoading, setCalendarLoading] = useState(true);
    const [calendarStatus, setCalendarStatus] = useState<CalendarSyncStatus | null>(null);

    const loadCalendarStatus = async () => {
        setCalendarLoading(true);
        try {
            const response = await fetch("/api/calendar/sync-status");
            if (!response.ok) throw new Error(await response.text());
            setCalendarStatus(await response.json());
        } catch (error) {
            console.error("Could not load calendar sync status", error);
        } finally {
            setCalendarLoading(false);
        }
    };

    useEffect(() => {
        loadCalendarStatus();
    }, []);

    const setCalendarSync = async (enabled: boolean) => {
        setCalendarLoading(true);
        try {
            const response = await fetch(
                enabled
                    ? "/api/calendar/sync-all-clients"
                    : "/api/calendar/disable-client-sync",
                { method: "POST" },
            );
            if (!response.ok) throw new Error(await response.text());
            await loadCalendarStatus();
        } catch (error) {
            setCalendarLoading(false);
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("profile.calendarSyncError"),
                comment: { name: appState.t("common.detail"), val: String(error) },
            });
        }
    };

    const disconnectCalendar = async () => {
        setCalendarLoading(true);
        try {
            const response = await fetch("/api/auth/calendar/google/disconnect", {
                method: "POST",
            });
            if (!response.ok) throw new Error(await response.text());
            await loadCalendarStatus();
        } catch (error) {
            setCalendarLoading(false);
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("profile.calendarSyncError"),
            });
        }
    };

    const refresh = () => appState.getPreferences();

    const openEditor = (pref: UserPreference | null) => {
        appState.appendModal({
            title: pref ? appState.t("profile.editPreference") : appState.t("profile.newPreference"),
            body: (
                <PreferenceEditor
                    preference={pref}
                    onClose={() => appState.popModal()}
                    onSaved={refresh}
                />
            ),
        });
    };

    const confirmDelete = (pref: UserPreference) => {
        appState.setModal({
            title: appState.t("profile.deletePreference"),
            body: appState.t("profile.deletePreferenceBody"),
            action: async () => {
                const doDelete = Request<DeleteUserPreferenceData, any>(
                    deleteUserPreference,
                    () => ({ body: { id: pref.id } }),
                    setLoading,
                    () => refresh(),
                    (error) => {
                        appState.setModal({
                            title: appState.t("common.error"),
                            body: error.detail?.toString() ?? appState.t("common.somethingWentWrong"),
                        });
                    },
                    () => true,
                );
                await doDelete(undefined as any);
            },
        });
    };

    const hasSpeakerProfile = (appState.user?.speaker_profile?.length ?? 0) > 0;

    return (
        <PageBox>
            <div className="flex flex-col justify-center gap-2 w-full max-w-[44rem] min-h-[calc(100dvh-4.5rem)] py-2">
                <div className="flex flex-row items-end justify-between gap-1 px-1">
                    <div className="inspector-header">
                        <h1 className="title">{appState.t("profile.title")}</h1>
                        {appState.user && (
                            <p className="detail-text">{appState.user.full_name} · {appState.user.email}</p>
                        )}
                    </div>
                    <LanguageSwitcher />
                </div>

                <Section title={appState.t("profile.calendarSyncTitle")} hint={appState.t("profile.calendarSyncDescription")}>
                    {calendarLoading && !calendarStatus && <EmptyRow>{appState.t("common.loading")}</EmptyRow>}
                    {calendarStatus && !calendarStatus.calendar_scope_granted && (
                        <Row
                            roomy
                            trailing={
                                <ActionHandler
                                    className="btn-sm shrink-0"
                                    disabled={calendarLoading}
                                    funq={() => { window.location.href = "/api/auth/calendar/google/connect"; }}
                                >
                                    {appState.t("profile.calendarSyncConnect")}
                                </ActionHandler>
                            }
                        >
                            <p className="l leading-[1.3]">{appState.t("profile.calendarSyncNotConnected")}</p>
                        </Row>
                    )}
                    {calendarStatus?.calendar_scope_granted && (
                        <>
                            <Row
                                roomy
                                trailing={
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={calendarStatus.sync_enabled}
                                        disabled={calendarLoading}
                                        onClick={() => setCalendarSync(!calendarStatus.sync_enabled)}
                                        className={`toggle ${calendarStatus.sync_enabled ? "toggle-on" : ""}`}
                                    />
                                }
                            >
                                <p className="l leading-[1.3]">
                                    {appState.t(
                                        calendarStatus.sync_enabled ? "profile.calendarSyncOn" : "profile.calendarSyncOff",
                                        { synced: calendarStatus.synced_count, total: calendarStatus.client_count },
                                    )}
                                </p>
                            </Row>
                            <div className="row-actions">
                                <button className="link-muted" disabled={calendarLoading} onClick={disconnectCalendar}>
                                    {appState.t("profile.calendarSyncDisconnect")}
                                </button>
                            </div>
                        </>
                    )}
                </Section>

                <Section title={appState.t("speaker.voiceProfile")}>
                    {hasSpeakerProfile ? (
                        <Row
                            roomy
                            trailing={
                                <ActionHandler
                                    className="btn-danger btn-sm shrink-0"
                                    funq={() => appState.setSpeakerProfile({ speaker_profile: [] })}
                                >
                                    {appState.t("speaker.clear")}
                                </ActionHandler>
                            }
                        >
                            <p className="l leading-[1.3]">{appState.t("speaker.hasProfile")}</p>
                            <p className="detail-text mt-[.35rem]">{appState.t("speaker.removeHint")}</p>
                        </Row>
                    ) : (
                        <div className="row-form">
                            <SpeakerProfileRecorder
                                onProfileCaptured={async (profile) => {
                                    let deviceLabel = appState.t("speaker.currentMic");
                                    try {
                                        const temporaryStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                                        deviceLabel = temporaryStream.getAudioTracks()[0]?.label || appState.t("speaker.currentMic");
                                        temporaryStream.getTracks().forEach(t => t.stop());
                                    } catch (err) {
                                        console.warn("Could not get microphone for label", err);
                                    }
                                    const updatedProfile = appendProfile(appState.user?.speaker_profile, deviceLabel, profile);
                                    appState.setSpeakerProfile({ speaker_profile: updatedProfile });
                                }}
                                existingProfile={appState.user?.speaker_profile}
                            />
                        </div>
                    )}
                </Section>

                <SkillsSection />

                <Section
                    title={appState.t("profile.preferences")}
                    action={
                        <ActionHandler
                            className="btn-ghost btn-sm"
                            title={appState.t("profile.addPreference")}
                            funq={() => openEditor(null)}
                            disabled={loading}
                        >
                            <Add_new size={16} />
                        </ActionHandler>
                    }
                >
                    {appState.preferences.length === 0 && (
                        <EmptyRow>{appState.t(loading ? "common.loading" : "profile.noPreferences")}</EmptyRow>
                    )}
                    {appState.preferences.map((pref) => (
                        <Row
                            key={pref.id}
                            roomy
                            onClick={() => openEditor(pref)}
                            trailing={
                                <span className="reveal shrink-0" onClick={(e) => e.stopPropagation()}>
                                    <ActionHandler
                                        className="btn-ghost btn-sm"
                                        title={appState.t("common.delete")}
                                        funq={() => confirmDelete(pref)}
                                        disabled={loading}
                                    >
                                        <Remove size={16} />
                                    </ActionHandler>
                                </span>
                            }
                        >
                            <p className="b whitespace-pre-wrap">{pref.content}</p>
                        </Row>
                    ))}
                </Section>
            </div>
        </PageBox>
    );
});
